// Internal helpers shared across booking strategies.
//
// These functions are identical across acc, camis, nswparks, webrezpro,
// waparks, stayrottnest, and campspot. Keeping them in one file means a
// change to resolution semantics (e.g. how we derive stay length when the
// caller passes both arrivalDate and departureDate) stays consistent.
//
// File prefixed with `_` to signal this module is private to the
// strategies folder — it is NOT part of the strategy API surface.

import { addDays, differenceInCalendarDays } from "date-fns";

/**
 * Resolve the checkout date from a strategy context. Prefers an explicit
 * `departureDate` when supplied; otherwise falls back to
 * `arrivalDate + (availabilityRequest.minStayLength ?? 1)`.
 */
export function resolveCheckout({
  arrivalDate,
  departureDate,
  availabilityRequest,
}) {
  if (departureDate) return departureDate;
  const stay = availabilityRequest?.minStayLength ?? 1;
  return addDays(arrivalDate, stay);
}

/**
 * Resolve the stay length in nights. Prefer an explicit arrival/departure
 * span when supplied; AvailabilityMatch links may be longer than the
 * request's stored minimum when the provider enforces a longer floor.
 */
export function resolveMinStayLength({
  arrivalDate,
  departureDate,
  availabilityRequest,
}) {
  if (arrivalDate && departureDate) {
    return differenceInCalendarDays(departureDate, arrivalDate);
  }
  if (availabilityRequest?.minStayLength != null) {
    return availabilityRequest.minStayLength;
  }
  return 1;
}

/**
 * Resolve the minimum capacity (party size). Prefers the availability
 * request's stored `minCapacity`; otherwise falls back to the incoming
 * `partySize`; otherwise `1`.
 */
export function resolveMinCapacity({ partySize, availabilityRequest }) {
  if (availabilityRequest?.minCapacity != null) {
    return availabilityRequest.minCapacity;
  }
  if (partySize != null) return partySize;
  return 1;
}
